import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL;

export interface StripeConfig {
  id: string;
  stripe_publishable_key: string | null;
  stripe_mode: 'test' | 'live';
  is_active: boolean;
  has_secret_key: boolean;
  has_webhook_secret: boolean;
  created_at: string;
  updated_at: string;
}

export async function getStripeConfiguration(token: string): Promise<StripeConfig | null> {
  const response = await axios.get(`${API_URL}/stripe/configuration`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.data;
}

export async function saveStripeConfiguration(
  data: {
    stripe_publishable_key?: string;
    stripe_secret_key?: string;
    stripe_webhook_secret?: string;
    stripe_mode: 'test' | 'live';
  },
  token: string
): Promise<StripeConfig> {
  const response = await axios.post(`${API_URL}/stripe/configuration`, data, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.data;
}

export async function testStripeConnection(token: string): Promise<{
  connected: boolean;
  mode: string;
  available: { amount: number; currency: string }[];
}> {
  const response = await axios.post(`${API_URL}/stripe/configuration/test`, {}, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.data;
}

export async function syncPlansToStripe(token: string): Promise<{ synced: number; skipped: number }> {
  const response = await axios.post(`${API_URL}/stripe/sync-plans`, {}, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.data;
}

export interface BillingLineItem {
  role: string;
  plan_name: string;
  count: number;
  unit_price: number;
  subtotal: number;
}

export interface BillingSummary {
  line_items: BillingLineItem[];
  total_monthly: number;
}

export async function getBillingSummary(companyId: string, token: string): Promise<BillingSummary> {
  const response = await axios.get(`${API_URL}/stripe/billing-summary/${companyId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.data;
}

export async function createStripeCustomer(companyId: string, token: string): Promise<{ stripe_customer_id: string }> {
  const response = await axios.post(`${API_URL}/stripe/customers/${companyId}`, {}, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.data;
}

export async function syncCompanySubscription(
  companyId: string,
  billingPeriod: 'monthly' | 'annual',
  token: string
): Promise<{ subscription_id: string; total_monthly: number }> {
  const response = await axios.post(`${API_URL}/stripe/subscriptions/${companyId}`, { billing_period: billingPeriod }, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.data;
}

/**
 * Create a Stripe Checkout Session (public — no auth token needed)
 */
export async function createCheckoutSession(
  companyId: string,
  email: string
): Promise<{ checkout_url: string }> {
  const response = await axios.post(`${API_URL}/stripe/create-checkout`, {
    company_id: companyId,
    email,
  });
  return response.data.data;
}
